import { css } from '@emotion/react';
import { useContext, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import NvIcon from 'shared/components/nv-icon';
import NvTable, { Column } from 'shared/components/nv-table';
import MentoringProgramContext, { MentoringProgramHomeUIContext } from 'athena/components/mentoring-program/context';
import { getMentorshipProgramGuides } from 'redux/actions/mentoring-program-guides';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import { getMentoringProgramGuidesList } from 'redux/selectors/mentorship-program-guides';
import GuideRow from './guide-row';
import CreateGuideFlyout from './create-guide-flyout';
import NoGuides from './no-guides';
import GuidesTurnedOff from './guides-turned-off';
import { config } from '@config/pendo.config.json';


enum ColumnID {
  APPEARS_IN = 'appears_in',
  TITLE = 'title',
  AUDIENCE = 'audience',
  OPTIONS = 'options',
}

type GuidesProps = {
  scrollEnabled: boolean;
  onScroll: React.ComponentProps<'div'>['onScroll'];
};

const Guides = ({
  scrollEnabled,
  onScroll,
}: GuidesProps) => {
  const styles = css`
    .table-body {
      overflow-y: ${scrollEnabled ? 'auto' : 'hidden'};
    }
  `;

  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { guidanceEnabled, id: mentoringProgramId } = mentoringProgram || {};
  const { withOverflowFlexGrowClass } = useContext(MentoringProgramHomeUIContext);

  const bodyProps = {
    onScroll,
    className: `table-body ${withOverflowFlexGrowClass}`,
  };

  const [showCreateGuideFlyout, setShowCreateGuideFlyout] = useState(false);
  const [guideFlyoutDefaultValues, setGuideFlyoutDefaultValues] = useState(null);

  const params = useMemo(() => ({}), []);

  const {
    reset,
    result,
    loadMore,
    isLoading,
    page,
  } = usePaginatedApi<MentoringProgramGuide, any>(
    (p) => dispatch(
      getMentorshipProgramGuides({
        mentoringProgramId,
      }),
    ).then(action => action.payload),
    params,
    getMentoringProgramGuidesList,
  );

  const getData = () => {
    if (isLoading && page === 1) return [];
    return result ?? [];
  };

  const guidesTableColumns: Column<MentoringProgramGuide>[] = useMemo(() => [
    {
      id: ColumnID.APPEARS_IN,
      header: t.MENTORING_PROGRAMS.GUIDES.COLUMNS.APPEARS_IN(),
      accessorFn: (guide) => guide.mentorshipProgramGuideRequirement,
      gridTemplateColumn: '3fr',
    },
    {
      id: ColumnID.TITLE,
      header: t.MENTORING_PROGRAMS.GUIDES.COLUMNS.TITLE(),
      accessorFn: (guide) => guide.title,
      gridTemplateColumn: '4fr',
    },
    {
      id: ColumnID.AUDIENCE,
      header: t.MENTORING_PROGRAMS.GUIDES.COLUMNS.AUDIENCE(),
      accessorFn: (guide) => guide.role,
      gridTemplateColumn: '2fr',
    },
    {
      id: ColumnID.OPTIONS,
      header: '',
      accessorFn: (guide) => guide,
      gridTemplateColumn: '1fr',
    },
  ], []);

  if (!guidanceEnabled) {
    return (
      <GuidesTurnedOff />
    );
  }

  return (
    <div css={styles} className={`d-flex flex-column ${withOverflowFlexGrowClass}`}>
      <div className='d-flex justify-content-end mb-4'>
        <Button
          variant='primary'
          onClick={() => setShowCreateGuideFlyout(true)}
          size='lg'
          className='d-flex flex-row align-items-center'
          aria-label={t.MENTORING_PROGRAMS.GUIDES.ADD_GUIDE()}
          data-qa={config.pendo.athena.mentorshipProgram.guides.addGuideButton}
        >
          <NvIcon icon='create-new-post' size='smallest' className='mr-2' />
          {t.MENTORING_PROGRAMS.GUIDES.ADD_GUIDE()}
        </Button>
      </div>
      <NvTable<MentoringProgramGuide>
        data={getData()}
        columns={guidesTableColumns}
        className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
        RowComponent={GuideRow}
        // TODO: this filter is a temporary fix for a bug where deleted guides were included in the list as undefined.
        // There should be a better solution which removes them from the list when they're deleted so this check isn't necessary.
        renderAfterRows={() => (!isLoading && result?.filter(g => g).length === 0 ? <NoGuides /> : null)}
        bodyProps={bodyProps}
        meta={{
          setShowCreateGuideFlyout,
          setGuideFlyoutDefaultValues,
        }}
      />
      {showCreateGuideFlyout && (
        <CreateGuideFlyout
          onClose={(shouldReset) => {
            setShowCreateGuideFlyout(false);
            setGuideFlyoutDefaultValues(null);
            if (shouldReset) {
              reset();
            }
          }}
          defaultValues={guideFlyoutDefaultValues}
        />
      )}
    </div>
  );
};

export default Guides;
